import React, {useState} from 'react';
import {Send} from 'lucide-react';
import {useQuotesApi} from "../context/ApiContext.tsx";
import SamButton from "./SamButton.tsx";
import CancelButton from "./CancelButton.tsx";

interface RejectQuoteProps {
    onSuccess: () => void;
    onCancel: () => void;
    quoteId: number | null;
}

export const RejectQuoteForm: React.FC<RejectQuoteProps> = ({
                                                                onSuccess,
                                                                onCancel,
                                                                quoteId
                                                            }) => {
    const [rejectReason, setRejectReason] = useState<string>('');
    const [error, setError] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);

    const quotesApi = useQuotesApi();

    const validateForm = (): boolean => {
        if (!rejectReason.trim()) {
            setError('Please enter a reason for rejection.');
            return false;
        }

        setError('');
        return true;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm() || !quoteId) {
            return;
        }

        try {
            setIsSending(true);
            // Call your API to send the rejection
            await quotesApi.rejectQuote({
                quoteId,
                rejectReason
            });
            // Call onSuccess to notify parent component
            onSuccess();
        } catch (error) {
            console.error("Error rejecting quote:", error);
            setError('Failed to reject quote. Please try again.');
        } finally {
            setIsSending(false);
        }
    };

    const handleCancel = () => {
        // Prevent cancellation during submission
        if (!isSending) {
            onCancel();
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label htmlFor="rejectReason" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Rejection
                </label>
                <textarea
                    id="rejectReason"
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Please explain why you're rejecting this quote..."
                    value={rejectReason}
                    onChange={(e) => {
                        setRejectReason(e.target.value);
                        if (error) setError('');
                    }}
                    disabled={isSending}
                />
                {error && (
                    <p className="mt-1 text-sm text-red-600">{error}</p>
                )}
            </div>

            <div className="flex justify-end space-x-2">
                <CancelButton
                    type="button"
                    onClick={handleCancel}
                    disabled={isSending}
                >
                    Cancel
                </CancelButton>
                <SamButton type="submit" disabled={isSending}>
                    <span className="inline-flex items-center space-x-1">
                        {isSending ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-2 border-t-transparent border-white rounded-full mr-1"></span>
                                <span>Sending...</span>
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4 mr-1" />
                                <span>Reject Quote</span>
                            </>
                        )}
                    </span>
                </SamButton>
            </div>
        </form>
    );
};